/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

import { useMemo } from "react"
import { useSelector } from "react-redux"
import { AppState } from "store/store.type"
import { SubDepartment } from "types/models/menu"
import { useMartItemMenuDisplayV2Query } from "queries"
import { useEnablePartialMenu } from "hooks/useEnablePartialMenu"
import { mapItemDepartmentToSubDepartment } from "utils/menuV3Mapper"
import { getMartSubDepartmentList } from "utils/menu_helper"

interface UseItemListSubDepartmentsReturn {
  subDepartments: SubDepartment[]
}

/**
 * Hook to get all sub-departments for ItemList component filters
 * When enablePartialMenu is true, fetches from query and flattens all sub-departments
 * Otherwise, uses Redux sub-departments
 */
export const useItemListSubDepartments = (): UseItemListSubDepartmentsReturn => {
  const enablePartialMenu = useEnablePartialMenu()

  // Get data from Redux
  const { merchantID, merchantGroupID, menu: reduxMenu } = useSelector(
    (state: AppState) => ({
      merchantID: state.menu.merchantID,
      merchantGroupID: state.menu.merchantGroupID,
      menu: state.menu.menu,
    }),
  )

  // Use martItemMenuDisplayV2 query when enablePartialMenu is true
  const { data: martMenuData } = useMartItemMenuDisplayV2Query(
    {
      merchantID,
      merchantGroupID,
    },
    {
      skip: !enablePartialMenu || !merchantID,
    },
  )

  // Get all sub-departments from query or menu
  const subDepartments = useMemo(() => {
    if (enablePartialMenu && martMenuData) {
      // Use query data when partial menu is enabled - flatten all sub-departments from all departments
      const allSubDepartments: SubDepartment[] = []
      martMenuData.departments?.forEach(department => {
        department.subDepartments?.forEach(subDept => {
          const mappedSubDept = mapItemDepartmentToSubDepartment(
            subDept,
            department.itemClassID,
            department.itemClassName,
          )
          allSubDepartments.push(mappedSubDept)
        })
      })
      return allSubDepartments
    }
    // When enablePartialMenu is false, use getMartSubDepartmentList(menu) (original logic)
    // This ensures sub-departments have totalItems populated correctly
    return getMartSubDepartmentList(reduxMenu)
  }, [enablePartialMenu, martMenuData, reduxMenu])

  return {
    subDepartments,
  }
}
